import { DateTime } from 'luxon';
import type { CalendarEvent } from './calendar.types';

/**
 * Activity-palette hexes (plan §1/§2) — identical to production `typeColors`.
 * Kept inline here so the Storybook mock is self-contained.
 */
const PALETTE = {
  urgent: '#E24D2A',
  pending: '#F9C431',
  solved: '#36B37E',
  note: '#7E4CC9',
  routines: '#4A90E2',
  campaign: '#D649A6',
} as const;

const TITLES: Record<keyof typeof PALETTE, string> = {
  urgent: 'Hydraulic leak flagged',
  pending: 'PM Service approaching',
  solved: 'Oil change completed',
  note: 'Field walkthrough note',
  routines: 'Daily Check confirmed',
  campaign: 'Spring inspection campaign',
};

const ASSETS = [
  'Tractor 01',
  'Forklift G7',
  'Grey Truck',
  'Weed Sprayer',
  'Pivot 4',
  'Cooler 4',
];

const mk = (
  id: string,
  at: DateTime,
  type: keyof typeof PALETTE,
  asset?: string,
): CalendarEvent => ({
  id,
  at,
  color: PALETTE[type],
  title: TITLES[type],
  asset,
  hour: at.hour,
});

/**
 * Realistic mock events anchored on the given month (defaults to the current
 * month). Exercises every Phase-1 visual branch:
 *   • varied colors from all 6 activity types
 *   • a few days with >3 events to trigger the month "+N more"
 *   • a couple of events outside 6am–6pm to show the week-view hour clamp
 *   • optional asset second line (showAsset)
 */
export const makeMockCalendarEvents = (
  anchor: DateTime = DateTime.now(),
): CalendarEvent[] => {
  const base = anchor.startOf('month');
  const types = Object.keys(PALETTE) as (keyof typeof PALETTE)[];
  const events: CalendarEvent[] = [];

  // Sprinkle 1–2 events across the first ~24 days, cycling colors.
  for (let d = 0; d < 24; d += 1) {
    if (d % 3 === 0) continue; // leave some empty days
    const day = base.plus({ days: d });
    const type = types[d % types.length];
    events.push(mk(`mock-${d}-a`, day.set({ hour: 8 + (d % 8) }), type, ASSETS[d % ASSETS.length]));
    if (d % 4 === 0) {
      const type2 = types[(d + 2) % types.length];
      events.push(mk(`mock-${d}-b`, day.set({ hour: 13 + (d % 4) }), type2, ASSETS[(d + 1) % ASSETS.length]));
    }
  }

  // A busy day (the 10th) with 5 events → month "+N more", all hours in-window.
  const busy = base.plus({ days: 9 });
  types.slice(0, 5).forEach((type, i) =>
    events.push(mk(`mock-busy-${i}`, busy.set({ hour: 7 + i * 2 }), type, ASSETS[i])),
  );

  // Out-of-window events (the 15th) → week-view clamp into first/last row.
  const clampDay = base.plus({ days: 14 });
  events.push(mk('mock-early', clampDay.set({ hour: 3 }), 'urgent', 'Cooler 4')); // before 6am
  events.push(mk('mock-late', clampDay.set({ hour: 21 }), 'note', 'Pivot 4')); // after 6pm

  return events;
};
